import React from "react";
import { QBOImportJob, QBOImportJobManager } from "../models/qbo-import-job";
import CheckCircleIcon from "@heroicons/react/24/solid/CheckCircleIcon";
import ExclamationTriangleIcon from "@heroicons/react/24/solid/ExclamationTriangleIcon";
import ClockIcon from "@heroicons/react/24/outline/ClockIcon";
import ArrowPathIcon from "@heroicons/react/24/outline/ArrowPathIcon";
import { useUserDisplayNamesStore } from "../store/user-display-names-map";

interface Props {
  importJob: QBOImportJob;
  className?: string;
  compact?: boolean;
}

export default function QBOImportProgressIndicator({
  importJob,
  className = "",
  compact = false,
}: Props): JSX.Element {
  const { status, data, timestampCreated, timestampLastModified, uid } =
    importJob;
  const { message, progress, stepProgress } = data;

  // Get user display names from store
  const { userDisplayNames } = useUserDisplayNamesStore();
  const displayName = userDisplayNames[uid] || uid; // Fallback to uid if display name not found

  // Format timestamps for display
  const formatTimestamp = (timestamp: any) => {
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      return "Unknown";
    }
  };

  const createdAt = formatTimestamp(timestampCreated);
  const lastModified = formatTimestamp(timestampLastModified);

  // Determine status color and icon
  const getStatusConfig = () => {
    const iconSize = compact ? "h-4 w-4" : "h-6 w-6";

    switch (status) {
      case "completed":
        return {
          color: "green",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          textColor: "text-green-800",
          progressColor: "bg-green-500",
          icon: <CheckCircleIcon className={`${iconSize} text-green-500`} />,
        };
      case "failed":
        return {
          color: "red",
          bgColor: "bg-red-50",
          borderColor: "border-red-200",
          textColor: "text-red-800",
          progressColor: "bg-red-500",
          icon: (
            <ExclamationTriangleIcon className={`${iconSize} text-red-500`} />
          ),
        };
      case "in-progress":
        return {
          color: "blue",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          textColor: "text-blue-800",
          progressColor: "bg-blue-500",
          icon: (
            <ArrowPathIcon
              className={`${iconSize} animate-spin text-blue-500`}
            />
          ),
        };
      case "pending":
      default:
        return {
          color: "gray",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          textColor: "text-gray-800",
          progressColor: "bg-gray-500",
          icon: <ClockIcon className={`${iconSize} text-gray-500`} />,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const overallProgress = Math.max(0, Math.min(100, progress));
  const currentStepProgress =
    typeof stepProgress === "number"
      ? Math.max(0, Math.min(100, stepProgress))
      : 0;

  if (compact) {
    return (
      <div
        className={`rounded-lg border ${statusConfig.borderColor} ${statusConfig.bgColor} p-4 shadow-sm ${className}`}
      >
        {/* Compact Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {statusConfig.icon}
            <div>
              <h4 className={`text-sm font-semibold ${statusConfig.textColor}`}>
                Import {QBOImportJobManager.readableStatus(status)}
              </h4>
              <p className="text-xs text-gray-600">
                Initiated by: {displayName}
              </p>
            </div>
          </div>
          {overallProgress >= 0 && (
            <div className="text-right">
              <div className={`text-lg font-bold ${statusConfig.textColor}`}>
                {overallProgress}%
              </div>
            </div>
          )}
        </div>

        {/* Compact Progress Bar */}
        {overallProgress >= 0 && (
          <div className="mb-2">
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className={`h-2 rounded-full transition-all duration-500 ease-out ${statusConfig.progressColor}`}
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Compact Status Message */}
        <p className="truncate text-xs text-gray-600">{message}</p>

        {/* Compact Progress Animation for In-Progress Status */}
        {status === "in-progress" && (
          <div className="mt-2 flex items-center gap-1">
            <div className="flex gap-1">
              <div
                className="h-1 w-1 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="h-1 w-1 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="h-1 w-1 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        )}

        {/* Compact Timestamps */}
        <div className="mt-3 flex justify-between text-xs italic text-gray-500">
          <span>Started: {createdAt}</span>
          <span>Updated: {lastModified}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border ${statusConfig.borderColor} ${statusConfig.bgColor} p-6 shadow-sm ${className}`}
    >
      {/* Header with status and icon */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {statusConfig.icon}
          <div>
            <h3 className={`text-lg font-semibold ${statusConfig.textColor}`}>
              Import {QBOImportJobManager.readableStatus(status)}
            </h3>
            <p className="text-sm text-gray-600">
              Initiated by: <span className="font-medium">{displayName}</span>
            </p>
          </div>
        </div>
        {overallProgress >= 0 && (
          <div className="text-right">
            <div className={`text-2xl font-bold ${statusConfig.textColor}`}>
              {overallProgress}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        )}
      </div>

      {/* Overall Progress Bar */}
      {overallProgress >= 0 && (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Overall Progress
            </span>
            <span className="text-sm text-gray-600">{overallProgress}%</span>
          </div>
          <div className="h-3 w-full rounded-full bg-gray-200">
            <div
              className={`h-3 rounded-full transition-all duration-500 ease-out ${statusConfig.progressColor}`}
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Step Progress Bar (if available) */}
      {typeof stepProgress === "number" && stepProgress > 0 && (
        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Step Progress
            </span>
            <span className="text-sm text-gray-600">
              {currentStepProgress}%
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className={`h-2 rounded-full transition-all duration-300 ease-out ${statusConfig.progressColor} opacity-75`}
              style={{ width: `${currentStepProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Status Message */}
      <div className="rounded-md bg-white/50 p-3">
        <p className="text-sm text-gray-700">
          <span className="font-medium">Status:</span> {message}
        </p>
      </div>

      {/* Progress Animation for In-Progress Status */}
      {status === "in-progress" && (
        <div className="mt-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <div className="flex gap-1">
              <div
                className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="h-2 w-2 animate-pulse rounded-full bg-blue-400"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error state additional info */}
      {status === "failed" && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-100 p-3">
          <p className="text-sm text-red-700">
            <span className="font-medium">Import failed.</span> Please try again
            or contact support if the issue persists.
          </p>
        </div>
      )}

      {/* Success state additional info */}
      {status === "completed" && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-100 p-3">
          <p className="text-sm text-green-700">
            <span className="font-medium">Import completed successfully!</span>{" "}
            Your data has been imported.
          </p>
        </div>
      )}

      {/* Timestamps at bottom */}
      <div className="mt-4 flex justify-between text-sm italic text-gray-500">
        <span>Started: {createdAt}</span>
        <span>Last Updated: {lastModified}</span>
      </div>
    </div>
  );
}
